import React, { FC, useEffect, useState } from "react";
import styles from "./Admin.module.scss";
import "../old/Attendees/Attendees.css";
import apiInstance from "../services/httpService";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Shape returned by /api/ccc/user/non-guests and /api/ccc/user/guests
interface UserRecord {
  id: number;
  name: string | null;
  email: string;
  vocal_part: string | null;
}

// Shape returned by /api/ccc/user/profile/view
interface FullProfile {
  id: number;
  name: string | null;
  email: string;
  dob: string | null;
  age: string | number | null;
  gender: string | null;
  vocal_part: string | null;
  phone: string | null;
  phone_2: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  choir: string | null;
}

interface ViewUserProps {
  /** Called when the user opens a detail view — adds a new breadcrumb level. */
  onSubSubSectionChange?: (label: string | null) => void;
}

const MIN_SEARCH  = 3;
const VOCAL_PARTS = ["Soprano", "Alto", "Tenor", "Bass"];
type TabKey = "ccc" | "guest";

const ViewUser: FC<ViewUserProps> = ({ onSubSubSectionChange }) => {
  // ── List state ─────────────────────────────────────────────────────────────
  const [cccUsers,   setCccUsers]   = useState<UserRecord[]>([]);
  const [guestUsers, setGuestUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading]       = useState(true);
  const [cccError,   setCccError]   = useState<string | null>(null);
  const [guestError, setGuestError] = useState<string | null>(null);
  const [activeTab, setActiveTab]   = useState<TabKey>("ccc");
  const [cccSearch,   setCccSearch]   = useState("");
  const [guestSearch, setGuestSearch] = useState("");

  // ── Detail state ───────────────────────────────────────────────────────────
  const [selectedUser,  setSelectedUser]  = useState<UserRecord | null>(null);
  const [detailProfile, setDetailProfile] = useState<FullProfile | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState<string | null>(null);

  // Vocal part edit state — starts read-only, pencil click enables the Select
  const [vocalPart,           setVocalPart]           = useState<string>("");
  const [isEditingVocalPart,  setIsEditingVocalPart]  = useState(false);
  const [saving, setSaving]                           = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const toast = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch user lists ───────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    Promise.allSettled([
      apiInstance.axiosCall("api/ccc/user/non-guests", { token }, "POST"),
      apiInstance.axiosCall("api/ccc/user/guests",     { token }, "POST"),
    ]).then(([cccResult, guestResult]) => {
      if (
        cccResult.status === "fulfilled" &&
        cccResult.value?.data?.status === "success"
      ) {
        setCccUsers(
          (cccResult.value.data.data || []).slice().sort(
            (a: UserRecord, b: UserRecord) => (a.name || "").localeCompare(b.name || "")
          )
        );
      } else {
        setCccError(
          cccResult.status === "fulfilled"
            ? cccResult.value?.data?.message || "Could not load CCC members."
            : "Could not load CCC members. Please try again."
        );
      }

      if (
        guestResult.status === "fulfilled" &&
        guestResult.value?.data?.status === "success"
      ) {
        setGuestUsers(
          (guestResult.value.data.data || []).slice().sort(
            (a: UserRecord, b: UserRecord) => (a.name || "").localeCompare(b.name || "")
          )
        );
      } else {
        setGuestError(
          guestResult.status === "fulfilled"
            ? guestResult.value?.data?.message || "Could not load guest members."
            : "Could not load guest members. Please try again."
        );
      }
      setLoading(false);
    });
  }, []);

  // ── Card click: fetch full profile, add breadcrumb level ──────────────────
  const handleCardClick = (user: UserRecord) => {
    setSelectedUser(user);
    setDetailProfile(null);
    setDetailError(null);
    setDetailLoading(true);
    setIsEditingVocalPart(false);
    onSubSubSectionChange?.("Details"); // adds new breadcrumb level, doesn't overwrite

    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall("api/ccc/user/profile/view", { token, user_id: user.id }, "POST")
      .then((res: any) => {
        if (res?.data?.status === "success") {
          const p: FullProfile = res.data.data;
          setDetailProfile(p);
          setVocalPart(p.vocal_part || "");
        } else {
          setDetailError(res?.data?.message || "Could not load user profile.");
        }
        setDetailLoading(false);
      })
      .catch(() => {
        setDetailError("Could not load user profile. Please try again.");
        setDetailLoading(false);
      });
  };

  // ── Revert vocal part to saved value ──────────────────────────────────────
  const handleRevertVocalPart = () => {
    setVocalPart(detailProfile?.vocal_part || "");
    setIsEditingVocalPart(false);
  };

  // ── Save vocal part ────────────────────────────────────────────────────────
  const handleSave = () => {
    if (!selectedUser || !detailProfile) return;
    const token = localStorage.getItem("token");
    setSaving(true);
    apiInstance
      .axiosCall(
        "api/ccc/user/update/user_bio",
        { token, user_id: selectedUser.id, vocal_part: vocalPart },
        "POST"
      )
      .then((res: any) => {
        setSaving(false);
        if (res?.data?.status === "success") {
          const updated = { ...detailProfile, vocal_part: vocalPart || null };
          setDetailProfile(updated);
          setIsEditingVocalPart(false);
          const updatedRecord = { ...selectedUser, vocal_part: vocalPart || null };
          setSelectedUser(updatedRecord);
          setCccUsers((prev) =>
            prev.map((u) => (u.id === selectedUser.id ? updatedRecord : u))
          );
          setGuestUsers((prev) =>
            prev.map((u) => (u.id === selectedUser.id ? updatedRecord : u))
          );
          toast(res.data.message || "Vocal part updated successfully.", "success");
        } else {
          toast(res?.data?.message || "Could not update.", "error");
        }
      })
      .catch((err: any) => {
        setSaving(false);
        toast(err?.response?.data?.message || "Could not update.", "error");
      });
  };

  // ── Disabled field helper ──────────────────────────────────────────────────
  const disabledField = (label: string, value: string | number | null, grid: string) => (
    <div className={`${grid} my-2`}>
      <TextField
        fullWidth
        label={label}
        value={value ?? ""}
        disabled
        variant="outlined"
        size="small"
      />
    </div>
  );

  // ── Loading (list) ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.Admin__loading}>
        <CircularProgress />
      </div>
    );
  }

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selectedUser) {
    if (detailLoading) {
      return (
        <div className={styles.Admin__loading}>
          <CircularProgress />
        </div>
      );
    }

    if (detailError) {
      return <div className={styles.Admin__empty}>{detailError}</div>;
    }

    if (detailProfile) {
      const isDirty = vocalPart !== (detailProfile.vocal_part || "");

      return (
        <div className="row col-lg-10 mx-auto my-4">

          {/* Header */}
          <div className={styles.Admin__user_detail_header}>
            <h3 className={styles.Admin__user_detail_title}>
              {detailProfile.name || "—"}
            </h3>
            <Button
              variant="contained"
              disabled={!isDirty || saving}
              onClick={handleSave}
              sx={{
                backgroundColor: "#3a8bac",
                "&:hover": { backgroundColor: "#195d7a" },
                textTransform: "none",
                fontFamily: "Montserrat-SemiBold",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>

          {/* ── Personal Details ── */}
          <div className={styles.Admin__user_detail_section}>Personal Details</div>

          {disabledField("Full Name",     detailProfile.name,   "col-lg-6")}
          {disabledField("Email",         detailProfile.email,  "col-lg-6")}
          {disabledField("Date of Birth", detailProfile.dob,    "col-lg-6")}
          {disabledField("Age",           detailProfile.age,    "col-lg-6")}
          {disabledField("Gender",        detailProfile.gender, "col-lg-6")}

          {/* Choir Part — read-only by default, pencil to edit, close to revert */}
          <div className="col-lg-6 my-2">
            {isEditingVocalPart ? (
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Choir Part</InputLabel>
                <Select
                  label="Choir Part"
                  value={vocalPart}
                  onChange={(e) => setVocalPart(e.target.value)}
                  input={
                    <OutlinedInput
                      label="Choir Part"
                      endAdornment={
                        <InputAdornment position="end" sx={{ mr: 1.5 }}>
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={handleRevertVocalPart}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <MenuItem value="">— None —</MenuItem>
                  {VOCAL_PARTS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                label="Choir Part"
                value={vocalPart || ""}
                disabled
                variant="outlined"
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setIsEditingVocalPart(true)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </div>

          {/* ── Contact Details ── */}
          <div className={styles.Admin__user_detail_section}>Contact Details</div>

          {disabledField("Phone",           detailProfile.phone,   "col-lg-6")}
          {disabledField("WhatsApp Number", detailProfile.phone_2, "col-lg-6")}

          {/* ── Address ── */}
          <div className={styles.Admin__user_detail_section}>Address</div>

          {disabledField("Address", detailProfile.address, "col-lg-12")}
          {disabledField("City",    detailProfile.city,    "col-lg-6")}
          {disabledField("State",   detailProfile.state,   "col-lg-6")}
          {disabledField("Country", detailProfile.country, "col-lg-6")}
          {disabledField("Pincode", detailProfile.pincode, "col-lg-6")}

          {/* Toast */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            <Alert
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </div>
      );
    }

    return null;
  }

  // ── List view ──────────────────────────────────────────────────────────────
  const applySearch = (list: UserRecord[], query: string): UserRecord[] => {
    const q = query.trim().toLowerCase();
    if (q.length < MIN_SEARCH) return list;
    return list.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.vocal_part || "").toLowerCase().includes(q)
    );
  };

  const cccFiltered   = applySearch(cccUsers,   cccSearch);
  const guestFiltered = applySearch(guestUsers, guestSearch);

  const renderCard = (user: UserRecord) => (
    <div
      className="col-lg-4 mt-3 px-2"
      key={user.id}
      style={{ cursor: "pointer" }}
      onClick={() => handleCardClick(user)}
    >
      <div className="attendees-item">
        <h3>{user.name || "—"}</h3>
        {user.vocal_part && <p>{user.vocal_part}</p>}
        <p className={styles.Admin__users_email}>{user.email}</p>
      </div>
    </div>
  );

  const renderTabContent = (
    list: UserRecord[],
    filtered: UserRecord[],
    search: string,
    setSearch: (v: string) => void,
    emptyLabel: string,
    tabError: string | null
  ) => (
    <>
      {tabError ? (
        <div className={styles.Admin__empty}>{tabError}</div>
      ) : (
        <>
          <div className={styles.Admin__users_search}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Search by name or vocal part"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
            />
          </div>
          {list.length === 0 ? (
            <div className={styles.Admin__empty}>{emptyLabel}</div>
          ) : filtered.length === 0 ? (
            <div className={styles.Admin__empty}>No members match your search.</div>
          ) : (
            <div className="col-lg-10 row mx-auto">
              {filtered.map(renderCard)}
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div className={styles.Admin__users}>
      {/* Tab bar */}
      <div className={styles.Admin__users_tabs}>
        <button
          type="button"
          className={`${styles.Admin__users_tab} ${activeTab === "ccc" ? styles.Admin__users_tab_active : ""}`}
          onClick={() => setActiveTab("ccc")}
        >
          CCC
          <span className={styles.Admin__users_tab_count}>{cccUsers.length}</span>
        </button>
        <button
          type="button"
          className={`${styles.Admin__users_tab} ${activeTab === "guest" ? styles.Admin__users_tab_active : ""}`}
          onClick={() => setActiveTab("guest")}
        >
          Guest
          <span className={styles.Admin__users_tab_count}>{guestUsers.length}</span>
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "ccc"
        ? renderTabContent(cccUsers,   cccFiltered,   cccSearch,   setCccSearch,   "No CCC members found.",   cccError)
        : renderTabContent(guestUsers, guestFiltered, guestSearch, setGuestSearch, "No guest members found.", guestError)}
    </div>
  );
};

export default ViewUser;
